const { LANGUAGE_ALIASES } = require('./constants');

/**
 * Compares one language code against another, taking into
 * account language codes with alternative names.
 *
 * @param {String} lang1  The first language code to compare.
 * @param {String} lang2  The second language code to compare.
 *
 * @returns {Boolean}  True if the language codes are equal, false otherwise.
 */
function languageEquals(lang1, lang2) {

	const lang1Aliases = LANGUAGE_ALIASES[lang1];
	const lang2Aliases = LANGUAGE_ALIASES[lang2];

	return (
		lang1 === lang2 ||
		lang1Aliases?.includes(lang2) ||
		lang2Aliases?.includes(lang1)
	);

}

/**
 * Adds a timeout restriction on a given Promise object.
 * If the Promise takes longer to resolve than the provided timeout
 * value, then the Promise will be rejected.
 *
 * @param {Object} promise  The promise to timeout.
 * @param {Number} timeoutValue  The maximum timeout value for the promise.
 *
 * @returns {*} The value of the resolved promise, if any.
 */
async function timeout(promise, timeoutValue) {

	// eslint-disable-next-line no-process-env
	if (process.env.NODE_ENV === 'test') {
		return await promise;
	}

	const data = await Promise.race([
		promise,
		new Promise((resolve, reject) => {
			setTimeout(() => reject(new Error('Request timed out')), timeoutValue);
		}),
	]);

	return data;

}

/**
 * Mimics a Promise with a randomized delay between the provided minimum
 * and maximum values in ms.
 *
 * @param {*} resolvedValue  The value for the Promise to resolve.
 * @param {Object} [options]  The function options.
 * @param {Number} options.min  The minimum delay in ms.
 * @param {Number} options.max  The maximum delay in ms.
 *
 * @returns {Promise}  A Promise object that resolves after a randomized delay.
 */
function mockRequest(resolvedValue, options = { min: 150, max: 350 }) {

	const { min, max } = options;

	const minCeiled = Math.ceil(min);
	const maxFloored = Math.floor(max);
	const requestTime = Math.floor((Math.random() * (maxFloored - minCeiled + 1)) + minCeiled);

	return new Promise((resolve) => setTimeout(() => {
		resolve(resolvedValue);
	}, requestTime));

}

module.exports = {
	languageEquals,
	mockRequest,
	timeout,
};
